import { useState } from "react";
import { Linkedin, Link as LinkIcon, Check, Rss } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "@/site/content";
import { track } from "@/site/analytics";

/**
 * Social connectors. The company pages do not exist yet, so each connector is
 * INERT (a disabled button with a "coming soon" tooltip, no navigation) until
 * its URL is set in .env:
 *   REACT_APP_LINKEDIN_URL  -> https://www.linkedin.com/company/<slug>
 *   REACT_APP_TWITTER       -> @handle  (X profile is derived from it)
 * The moment a value is present the same element becomes a real link. No code change.
 */

// lucide has no X-logo glyph; this is the standard mark, sized like the others.
const XLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const xProfile = (handle) => (handle ? `https://x.com/${handle.replace(/^@/, "")}` : "");

export const CONNECTORS = () => [
  { key: "linkedin", label: "LinkedIn", url: SITE.linkedin, Icon: Linkedin },
  { key: "x", label: "X", url: xProfile(SITE.twitter), Icon: XLogo },
];

export function SocialConnector({ c, size = 16, where = "footer" }) {
  const cls = `social${c.url ? "" : " social--soon"}`;
  if (!c.url) {
    return (
      <button type="button" className={cls} aria-disabled="true" title={`${c.label} — coming soon`} aria-label={`${c.label} (coming soon)`}
              onClick={(e) => e.preventDefault()} data-testid={`social-${c.key}`}>
        <c.Icon size={size} />
      </button>
    );
  }
  return (
    <a className={cls} href={c.url} target="_blank" rel="noopener noreferrer" aria-label={`Postern on ${c.label}`} title={`Postern on ${c.label}`}
       onClick={() => track("social_click", { network: c.key, where })} data-testid={`social-${c.key}`}>
      <c.Icon size={size} />
    </a>
  );
}

export function SocialRow({ size = 16, where = "footer", withRss = false }) {
  return (
    <div className="social-row" data-testid={`social-row-${where}`}>
      {CONNECTORS().map((c) => <SocialConnector key={c.key} c={c} size={size} where={where} />)}
      {withRss && <Link className="social" to="/blog/subscribe" title="Subscribe to the blog (RSS, Slack, feed readers)" aria-label="Subscribe to the blog" data-testid="social-rss"><Rss size={size} /></Link>}
    </div>
  );
}

/**
 * Share row for a post. "Copy link" always works. The LinkedIn / X share
 * intents are gated on the same env values as the connectors, so nothing
 * redirects off-site until the company presence exists.
 */
export function ShareRow({ url, title }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* older browsers: fall through, still show state */ }
    setCopied(true); track("article_share", { network: "copy" }); setTimeout(() => setCopied(false), 1800);
  };
  const intents = [
    { key: "linkedin", label: "LinkedIn", ok: !!SITE.linkedin, Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
    { key: "x", label: "X", ok: !!SITE.twitter, Icon: XLogo, href: `https://x.com/intent/post?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}` },
  ];
  return (
    <div className="share" data-testid="article-share">
      <span className="share__label">Share</span>
      <button type="button" className="share__btn" onClick={copy} data-testid="share-copy">{copied ? <Check size={14} /> : <LinkIcon size={14} />}{copied ? "Copied" : "Copy link"}</button>
      {intents.map((i) => i.ok ? (
        <a key={i.key} className="share__btn" href={i.href} target="_blank" rel="noopener noreferrer" onClick={() => track("article_share", { network: i.key })} data-testid={`share-${i.key}`}><i.Icon size={14} />{i.label}</a>
      ) : (
        <button key={i.key} type="button" className="share__btn share__btn--soon" aria-disabled="true" title={`Share to ${i.label} — coming soon`} onClick={(e) => e.preventDefault()} data-testid={`share-${i.key}`}><i.Icon size={14} />{i.label}</button>
      ))}
    </div>
  );
}
